import { Fragment, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { AppDataGrid, type AppColumn } from "../../components/AppDataGrid";
import { AppDialog } from "../../components/AppDialog";
import { useNotify } from "../../components/notifications";
import { AiCombo } from "../../components/AiCombo";

/**
 * Test Code → **Formula test** (prototype Test-Code-v12 §v7, `openRoundTest`):
 * "type raw results and see exactly what will be reported", with every legacy
 * formula step named — CalculateRL → CalculateNumericResult →
 * CalculateResultWithDF → ApplyRoundingRules — and the three QC tabs adding
 * CalculateDupSourceResult / CalculateRPD / RoundRPD, the spike-source and
 * Recovery pair, and EvaluateQCStatus.
 *
 * LAYOUT follows the prototype's `rb-grid`: the RULE pane on the left (which
 * rule is in force, and which QC type / parameter it belongs to), the test bench
 * on the right (tab strip → formula sequence → mode + DF → quick sets → raw
 * values → the result table).
 *
 * ⚠️ NOTHING IS CALCULATED HERE. Every value comes from
 * `POST /api/v1/test-methods/{id}/round-test`, which runs the lab's one rounder
 * (`LimsRounding.Apply`, backed by the legacy E_RoundOff library). The prototype
 * does the arithmetic in JavaScript and says so in its own notes: "in the build
 * the tool must call the single place that rounds, so the test tool, result entry
 * and reports always agree". A preview computed in the browser would be a second
 * rounder that could quietly disagree with the reported result — the exact
 * failure this tool exists to catch. The only thing the browser decides is WHICH
 * numbers to try (the quick sets), which is not business logic.
 *
 * ⚠️ ONE DELIBERATE DIFFERENCE from the prototype. Its left pane is captioned
 * "Rule — edits go into this test (Save to keep)" and writes straight into the
 * grid row it came from. Alpaca Intelligence's Sample Parameters / QC Limits
 * grids are READ-ONLY today — there is no parameter-update endpoint — so this
 * pane SHOWS the saved rule instead of editing it. Making it editable is a write
 * path with its own audit trail, not a layout change; it lands when the grids do.
 */

type Step = { function: string; detail: string };
type Row = { raw: string; reported: string; steps: Step[]; parameter?: string; units?: string | null };
type Rule = {
  rl?: string | null;
  mdl?: string | null;
  sigFig?: string | null;
  cutOff?: string | null;
  decimals?: string | null;
  defaultResult?: string | null;
  units?: string | null;
};
/** The QC limit row behind a QC tab — the control limit, the spike and the
 *  RPD / %Rec rounding, as the API echoes them back. */
type QcLimits = {
  ucLimit?: string | null;
  rpdCtrlLimit?: string | null;
  spikeAmount?: string | null;
  recLcLimit?: string | null;
  recUcLimit?: string | null;
  rpdSigFig?: string | null;
  rpdCutOff?: string | null;
  rpdDecimal?: string | null;
  recSigFig?: string | null;
  recCutOff?: string | null;
  recDecimal?: string | null;
};
type Preview = {
  parameter?: string;
  rule: Rule;
  result: { effectiveRl?: string | null; sequence: string[]; rows: Row[] };
};

/** The prototype's four tabs. */
type Kind = "sample" | "blank" | "rpd" | "rec";
/** Sample tab only: many values against one parameter, or one value against all. */
type Mode = "one" | "all";

/** A QC preview row: the inputs echoed back, the computed value, pass/fail. */
type QcRow = { columns: string[]; computed: string; status: "Pass" | "Fail" | "Blank"; steps: Step[] };
type QcPreview = {
  parameter?: string;
  qcType?: string;
  rule: Rule;
  qc?: QcLimits;
  result: { sequence: string[]; rows: QcRow[] };
};

/** What the result grid shows: a sample row and a QC row share their narrated
 *  steps and differ in the rest, so the grid reads them as one shape with the
 *  tab-specific fields optional. */
type GridRow = { steps: Step[] } & Partial<Omit<Row, "steps">> & Partial<Omit<QcRow, "steps">>;

/** A QC type this test carries, as the picker needs it. */
export type RoundTestQcType = {
  id: string;
  qcType?: string | null;
  qcRole?: string | null;
  qcRootRole?: string | null;
  /** Drives the Recovery caption: only a Sample / MS source has a native value. */
  qcSource?: string | null;
};

/** Which QC KINDS each tab applies to — a Blank tab offers only blank types, a
 *  Recovery tab only spikes (prototype RB_KINDS, v12 line 1107). */
const KIND_ROLES: Record<Exclude<Kind, "sample">, readonly string[]> = {
  blank: ["blank"],
  rpd: ["dup", "spdup"],
  rec: ["spike", "spdup"],
};

const KIND_LABEL: Record<Kind, string> = {
  sample: "Sample result",
  blank: "Blank",
  rpd: "RPD",
  rec: "Recovery",
};

/** The prototype's starting values per tab (v12 RB.kv). */
const KIND_VALUES: Record<Kind, string> = {
  sample: "0.30\n0.48\n0.5\n0.995\n1.005\n3.1450\n3.1550\n12.345\n123.45\nND\nn.a.",
  blank: "0.12\n0.31\n0.5\n0.62\nND",
  rpd: "10, 12\n12.3, 11.8\n0.3, 0.8\n0.2, 0.3\nND, 0.6",
  rec: "48.7\n52.1\n41.0\n58.9",
};

/** Column headers per QC tab, matching what the API echoes back. */
const KIND_COLS: Record<Exclude<Kind, "sample">, readonly string[]> = {
  blank: ["Raw"],
  rpd: ["Dup", "Source", "Results"],
  rec: ["Measured", "Source", "Spike"],
};

/** Which rule fields each tab's left pane shows, in the prototype's order
 *  (v12 rbSide): the sample measuring rule, or the QC type's own limits. */
const RULE_FIELDS: Record<Kind, ReadonlyArray<readonly [string, string]>> = {
  sample: [["RL", "rl"], ["SigFig", "sigFig"], ["CutOff", "cutOff"], ["Decimal", "decimals"]],
  blank: [["UCLimit", "ucLimit"], ["RL", "rl"], ["SigFig", "sigFig"], ["CutOff", "cutOff"], ["Decimal", "decimals"]],
  rpd: [["RPDCtrlLimit", "rpdCtrlLimit"], ["RPD SigFig", "rpdSigFig"], ["RPD CutOff", "rpdCutOff"],
        ["RPD Decimal", "rpdDecimal"]],
  rec: [["SpikeAmount", "spikeAmount"], ["RecLCLimit", "recLcLimit"], ["RecUCLimit", "recUcLimit"],
        ["REC SigFig", "recSigFig"], ["REC CutOff", "recCutOff"], ["REC Decimal", "recDecimal"]],
};

export type RoundTestParam = {
  oid: string;
  parameter?: string;
  rl?: string | null;
  cutOff?: string | null;
  retired?: boolean;
};

/** The prototype's quick sets (RQUICK). They are INPUT GENERATORS, not results,
 *  so they stay in the browser — picking which numbers to try is not a rule. */
const QUICK: ReadonlyArray<readonly [string, string, (p: RoundTestParam) => string[]]> = [
  ["cut", "Around CutOff", (p) => {
    const c = Number(p.cutOff) || 1;
    return [c * 0.995, c * 0.999, c, c * 1.005, c * 1.05].map((x) => String(+x.toPrecision(6)));
  }],
  ["rl", "Around RL", (p) => {
    const l = Number(p.rl) || 0.5;
    return [l * 0.5, l * 0.99, l, l * 1.01, l * 2].map((x) => String(+x.toPrecision(5)));
  }],
  ["chem", "Chemistry rule (half → even)",
    () => ["3.1450", "3.1550", "2.25", "2.35", "12.25", "12.35", "0.125", "0.135"]],
  ["wide", "Wide range",
    () => ["0.000412", "0.0123", "0.456", "3.14159", "12.345", "123.45", "4567.8", "98765", "1234567"]],
] as const;

/** The prototype's caption for the values box, per tab (v12 rbMain `ph`). */
const valuesCaption = (kind: Kind, hasNativeSource: boolean) =>
  kind === "blank" ? "Blank raw readings — one per line"
  : kind === "rpd" ? "Duplicate, Source — raw readings, one pair per line (e.g. 10, 12)"
  : kind === "rec"
    ? hasNativeSource
      ? "Measured, Source — raw readings, one per line (e.g. 0.985, 0.02)"
      : "Measured raw reading — one per line (no native source → source = 0)"
    : "Raw readings — one per line (numbers, ND or n.a.)";

/** The CutOff that matches a SigFig + Decimal pair (prototype rIdealCut): the
 *  value at which rounding switches from decimals to significant figures. A
 *  stored CutOff far from it is a data-quality signal, so the pane says so. */
const idealCutOff = (sf?: string | null, dec?: string | null) => {
  const s = parseInt(String(sf ?? ""), 10);
  const d = parseInt(String(dec ?? ""), 10);
  if (!(s > 0) || Number.isNaN(d)) return null;
  return Number(Math.pow(10, s - 1 - d)).toFixed(Math.max(d, 0));
};

export function TestRoundingDialog({
  open,
  base,
  params,
  testName,
  initialOid,
  qcTypes = [],
  initialKind = "sample",
  initialQcTypeId,
  onClose,
}: {
  open: boolean;
  base: string;
  /** The tab's live Sample Parameter rows — the picker and the quick sets read these. */
  params: RoundTestParam[];
  /** The test this tool belongs to — the prototype titles the dialog with it
   *  ("Formula test — VOC"), not with the tab, which the tab strip already shows. */
  testName?: string;
  /** The ticked row, if any; otherwise the first parameter. */
  initialOid?: string;
  /** The QC types this test carries; the three QC tabs pick from them. */
  qcTypes?: RoundTestQcType[];
  /** Which tab to open on — "Test limits" opens the tab for the QC type's kind. */
  initialKind?: Kind;
  initialQcTypeId?: string;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<Kind>("sample");
  const [mode, setMode] = useState<Mode>("one");
  const [qcTypeId, setQcTypeId] = useState("");
  const [oid, setOid] = useState("");
  const [text, setText] = useState(KIND_VALUES.sample);
  const [single, setSingle] = useState("0.75");
  const [df, setDf] = useState("1");
  /* Notation (prototype rbSide). PREVIEW ONLY: the ALPACA formulas have no
     notation step and dbo.Testparameter has no column for it — the prototype
     calls it "an AI proposal" — so this is a switch like Result formula, not a
     value on the record, and nothing is stored. */
  const [notation, setNotation] = useState("Standard");
  /** The grid's row ticks — the shared select-all + row checkbox column every
   *  AI grid carries. Nothing acts on a preview row, so the selection is the
   *  reader's own marker (follow a line down the Steps column, copy one row
   *  out); it is cleared whenever the result set is replaced. */
  const [rowSel, setRowSel] = useState<Set<string | number>>(new Set());
  const [useDf, setUseDf] = useState(true);
  const [data, setData] = useState<Preview | null>(null);
  const [qcData, setQcData] = useState<QcPreview | null>(null);
  const [busy, setBusy] = useState(false);
  /* "Not set up on this QC type" is a NORMAL state, not a failure: a QC type can
     be linked to a test (QC Types tab) and carry no limit row for the parameter
     you were just looking at — switching Sample result → Blank lands on exactly
     that. The prototype says so in the pane (v12 rbSide:1139); a blocking Error
     dialog made an ordinary tab switch look broken (owner report 2026-10-20). */
  const [note, setNote] = useState<string | null>(null);
  const notify = useNotify();

  useEffect(() => {
    if (!open) return;
    setKind(initialKind);
    setMode("one");
    setQcTypeId(initialQcTypeId ?? "");
    setOid(initialOid || params[0]?.oid || "");
    setText(KIND_VALUES[initialKind]);
    setSingle("0.75");
    setDf("1");
    setUseDf(true);
    setNotation("Standard");
    setRowSel(new Set());
    setNote(null);
    setData(null);
    setQcData(null);
    // The params ARRAY is rebuilt by the parent on every render; depending on it
    // would re-run this reset (wiping the typed values) on an unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialOid, initialKind, initialQcTypeId, params[0]?.oid]);

  /** Prototype qcKindOf (v12 line 499) — Spike and Standard are ONE kind. */
  const kindOf = (t: RoundTestQcType) =>
    t.qcRootRole === "Blank" ? "blank"
    : t.qcRole === "Duplicate" ? (t.qcRootRole === "Duplicate" ? "dup" : "spdup")
    : "spike";

  /** The QC types this tab can run against. */
  const typesForKind = useMemo(
    () => (kind === "sample" ? [] : qcTypes.filter((t) => KIND_ROLES[kind].includes(kindOf(t)))),
    [kind, qcTypes],
  );

  // Keep the picked QC type valid for the tab — switching tabs otherwise leaves
  // a type the new tab cannot run against.
  useEffect(() => {
    if (kind === "sample") return;
    if (!typesForKind.some((t) => t.id === qcTypeId)) setQcTypeId(typesForKind[0]?.id ?? "");
  }, [kind, typesForKind, qcTypeId]);

  const picked = useMemo(() => params.find((p) => p.oid === oid), [params, oid]);
  const pickedType = useMemo(() => qcTypes.find((t) => t.id === qcTypeId), [qcTypes, qcTypeId]);
  const hasNativeSource = pickedType?.qcSource === "Sample" || pickedType?.qcSource === "MS";
  /** A QC tab runs on one parameter of one QC type, so the "all parameters"
   *  mode is the sample tab's only (prototype draws the toggle there alone). */
  const allMode = kind === "sample" && mode === "all";

  // Re-run whenever anything that feeds the preview changes. Debounced, because
  // this fires on every keystroke in the values box.
  useEffect(() => {
    if (!open || !oid) return;
    // A QC tab needs a QC type before it can run — the limits live on that row.
    if (kind !== "sample" && !qcTypeId) {
      setData(null);
      setQcData(null);
      return;
    }
    const values = (allMode ? [single] : text.split("\n")).map((v) => v.trim()).filter(Boolean);
    if (!values.length) {
      setData(null);
      setQcData(null);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      void (async () => {
        setBusy(true);
        try {
          const { data: res } = await api.post(
            `${base}/round-test`,
            {
              parameterOid: oid, values, df, useDf, notation,
              kind,
              qcTypeOid: kind === "sample" ? null : qcTypeId,
              allParameters: allMode,
            },
            { silentLoading: true } as never,   // keeps the preview out of the global loader
          );
          if (!alive) return;
          // A fresh result set replaces the rows the ticks pointed at — row 3 of
          // the old values is not row 3 of the new ones.
          setRowSel(new Set());
          setNote(null);
          if (kind === "sample") { setData(res?.data ?? null); setQcData(null); }
          else { setQcData(res?.data ?? null); setData(null); }
        } catch (e: unknown) {
          if (alive) {
            setData(null);
            setQcData(null);
            const err = e as { response?: { status?: number; data?: { error?: { message?: string } } } };
            if (err?.response?.status === 404) {
              // The parameter simply is not on this QC type — the prototype's
              // own wording, in the pane, and the grid stays empty.
              setNote(
                `${picked?.parameter ?? "This parameter"} is not set up on ${
                  pickedType?.qcType ?? "this QC type"
                }. Add it in QC Limits.`,
              );
            } else {
              setNote(null);
              notify.error(
                err?.response?.data?.error?.message ?? "Could not run the rounding preview.",
              );
            }
          }
        } finally {
          if (alive) setBusy(false);
        }
      })();
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // notify is stable; excluding it keeps this from re-running on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, oid, text, single, allMode, df, useDf, notation, base, kind, qcTypeId]);

  if (!open) return null;

  const rule = data?.rule ?? qcData?.rule;
  const limits = qcData?.qc;

  /** The rule value the left pane prints for one field — a QC tab's own limits
   *  first, then the measuring rule the preview ran with. */
  const ruleValue = (key: string): string => {
    const fromQc = limits ? (limits as Record<string, string | null | undefined>)[key] : undefined;
    const fromRule = rule ? (rule as Record<string, string | null | undefined>)[key] : undefined;
    return String((fromQc ?? fromRule) ?? "") || "—";
  };

  /* ── What the grid shows ──────────────────────────────────────────────── */

  /** Steps as the narrated block the prototype prints — the function name, then
   *  what it did. Rendered, not stored, so the export still gets plain text. */
  const stepsCell = (steps: Step[]) => (
    <div className="tr-why">
      {steps.map((st, j) => (
        <div key={j}>
          <span className="tr-fn">{st.function}</span> {st.detail}
        </div>
      ))}
    </div>
  );
  const stepsText = (steps: Step[]) => steps.map((st) => `${st.function} ${st.detail}`).join(" · ");

  const gridRows: GridRow[] =
    kind === "sample" ? (data?.result.rows ?? []) : (qcData?.result.rows ?? []);

  const gridCols = ((): AppColumn<GridRow>[] => {
    /* Steps is the SAME width on every tab. Left to the grid's auto layout it
       takes whatever is left over, so it was wide on Sample result and squeezed
       on RPD / Recovery, which carry three more columns — the same narration
       reading differently depending on which tab you were on. width + minWidth +
       maxWidth together pin it (AppColumn: under auto table-layout a column
       otherwise grows to its content), and the QC tabs scroll horizontally
       instead of crushing it. */
    const steps: AppColumn<GridRow> = {
      field: "steps", header: <span className="rb-steps-h">Steps</span>, headerText: "Steps",
      wrap: true, sortable: false, filterable: false,
      width: 470, minWidth: 470, maxWidth: 470,
      render: (r) => stepsCell(r.steps), text: (r) => stepsText(r.steps),
    };

    if (kind === "sample") {
      return [
        ...(allMode
          ? [{ field: "parameter", header: "Parameter", width: 170, align: "center" } as AppColumn<GridRow>]
          : []),
        { field: "raw", header: "Raw", width: 90, align: "center" },
        {
          field: "reported", header: "Result", width: 110, align: "center",
          render: (r) => (
            <>
              {r.reported}
              {(r.units ?? rule?.units) ? <small> {r.units ?? rule?.units}</small> : null}
            </>
          ),
        },
        steps,
      ];
    }

    return [
      ...KIND_COLS[kind].map((c, n): AppColumn<GridRow> => ({
        field: `col${n}`, header: c, width: n < 2 ? 100 : 130,
        align: "center",
        value: (r) => r.columns?.[n] ?? "",
      })),
      { field: "computed", header: kind === "blank" ? "Result" : kind === "rpd" ? "RPD" : "%Rec",
        width: 100, align: "center" },
      steps,
      {
        field: "status", header: "QCStatus", type: "status", width: 110, align: "center",
        // Blank = nothing to judge; an empty cell says that without inventing a
        // verdict, and keeps it out of the status filter's value list.
        value: (r) => (r.status === "Blank" ? "" : r.status),
      },
    ];
  })();

  const emptyText =
    kind === "sample"
      ? busy ? "Working…" : "Type a raw result to see what would be reported."
      : busy ? "Working…"
      // The grid says the same thing the pane does, rather than inviting a value
      // that cannot run against this QC type.
      : note ? note
      : qcTypeId ? "Type a value to see what this QC type would report."
      : "Pick a QC type and parameter.";

  /** Prototype's CutOff sanity check, shown as a note (it cannot be fixed from
   *  here while the grids are read-only). */
  const badCutOff = (() => {
    if (kind !== "sample" || !rule?.cutOff) return null;
    const ideal = idealCutOff(rule.sigFig, rule.decimals);
    const co = Number(rule.cutOff);
    if (ideal === null || Number.isNaN(co)) return null;
    const i = Number(ideal);
    return co < i - 1e-12 || co > i * 10 + 1e-12 ? ideal : null;
  })();

  return (
    <AppDialog
      open={open}
      title={`Formula test — ${testName || KIND_LABEL[kind]}`}
      width={1100}
      onClose={onClose}
      className="dlg-center"
      /* The prototype's pair: Close (primary) and Cancel. Both simply close —
         the tool writes nothing, so there is no half-done state to discard —
         and keeping both means the footer reads the same as every other dialog
         on this form. */
      footer={
        <>
          <button className="ca-btn ca-btn-solid" onClick={onClose}>Close</button>
          <button className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div className="rb-grid">
        {/* ── LEFT: the rule in force ──────────────────────────────────────── */}
        <div className="rb-side">
          <div className="rb-fgrid">
            {kind !== "sample" && (
              <>
                <label>QC type</label>
                <AiCombo
                  value={qcTypeId || null}
                  options={typesForKind.map((t) => ({ value: t.id, label: t.qcType ?? "" }))}
                  placeholder={typesForKind.length ? "— Select —" : "(none on this test)"}
                  onChange={(v) => setQcTypeId(v ?? "")}
                />
              </>
            )}

            <label>Parameter</label>
            <AiCombo
              value={oid || null}
              options={params
                .filter((p) => kind === "sample" || !p.retired)
                .map((p) => ({ value: p.oid, label: `${p.parameter ?? ""}${p.retired ? " (retired)" : ""}` }))}
              placeholder="— Select —"
              onChange={(v) => setOid(v ?? "")}
            />

            {RULE_FIELDS[kind].map(([label, key]) => (
              <Fragment key={label}>
                <label>{label}</label>
                <input className="sd-input rb-ro" value={ruleValue(key)} readOnly tabIndex={-1} />
              </Fragment>
            ))}

            {kind === "sample" && (
              <>
                <label>Notation</label>
                <select
                  className="sd-input rb-sel"
                  value={notation}
                  onChange={(e) => setNotation(e.target.value)}
                >
                  <option value="Standard">Standard</option>
                  <option value="Scientific">Scientific</option>
                  <option value="Auto">Auto</option>
                </select>
              </>
            )}

            <label>Result formula</label>
            <select
              className="sd-input rb-sel"
              value={useDf ? "1" : "0"}
              onChange={(e) => setUseDf(e.target.value === "1")}
            >
              <option value="1">CalculateResultWithDF (× DF)</option>
              <option value="0">CalculateResult (no DF)</option>
            </select>
          </div>

          {note && <div className="tc-copy-note tc-copy-note--warn">{note}</div>}

          {kind !== "sample" && !typesForKind.length && (
            <div className="tc-copy-note tc-copy-note--warn">
              This test has no {KIND_LABEL[kind].toLowerCase()} QC type.
            </div>
          )}

          {badCutOff && (
            <div className="tc-copy-note tc-copy-note--warn">
              CutOff {rule?.cutOff} doesn&apos;t match {rule?.sigFig} SF + {rule?.decimals} dp
              (expected {badCutOff}).
            </div>
          )}

        </div>

        {/* ── RIGHT: the bench ─────────────────────────────────────────────── */}
        <div className="rb-main">
          <div className="tr-tabs">
            {(Object.keys(KIND_LABEL) as Kind[]).map((k) => (
              <button
                key={k}
                type="button"
                className={`tc-qcview-chip${kind === k ? " on" : ""}`}
                onClick={() => { setKind(k); setText(KIND_VALUES[k]); setRowSel(new Set()); setNote(null); }}
              >
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>

          <div className="rb-modebar">
            {kind === "sample" && (
              <>
                <span className="rb-seg">
                  <button
                    type="button"
                    className={`tc-qcview-chip${mode === "one" ? " on" : ""}`}
                    onClick={() => { setMode("one"); setRowSel(new Set()); }}
                  >
                    Many values · one parameter
                  </button>
                  <button
                    type="button"
                    className={`tc-qcview-chip${mode === "all" ? " on" : ""}`}
                    onClick={() => { setMode("all"); setRowSel(new Set()); }}
                  >
                    One value · all parameters
                  </button>
                </span>
                {allMode && (
                  <>
                    <label className="tc-copy-lbl tc-lbl-plain">Raw</label>
                    <input
                      className="sd-input tr-df rb-raw"
                      value={single}
                      onChange={(e) => setSingle(e.target.value)}
                    />
                  </>
                )}
              </>
            )}
            <label className="tc-copy-lbl tc-lbl-plain">DF</label>
            <input
              className="sd-input tr-df"
              value={df}
              onChange={(e) => setDf(e.target.value)}
              disabled={!useDf}
              title="Dilution factor"
            />
          </div>

          {kind === "sample" && mode === "one" && (
            <div className="tr-quick">
              <span className="tc-chips-lbl">Quick sets:</span>
              {QUICK.map(([k, label, gen]) => (
                <button
                  key={k}
                  type="button"
                  className="tc-chip"
                  disabled={!picked}
                  onClick={() => picked && setText(gen(picked).join("\n"))}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {!allMode && (
            <>
              <textarea
                className="sd-input tr-vals"
                rows={4}
                value={text}
                placeholder={valuesCaption(kind, hasNativeSource)}
                onChange={(e) => setText(e.target.value)}
              />
            </>
          )}

          {/* The shared grid, not a hand-rolled table (standing rule 7): column
              separators, zebra, row hover, resizable columns, the Excel-style
              header filter, Export to + Maximize and the standard footer all
              come with it. Steps WRAPS — it is 4-7 narrated lines per row — and
              QCStatus is a type:'status' column, so Pass / Fail take the shared
              statusPill (ok green / alert red) instead of hand-coloured text. */}
          <div className="rb-out">
            <AppDataGrid
              key={`${kind}-${allMode ? "all" : "one"}`}
              rows={gridRows}
              loading={busy}
              columns={gridCols}
              getRowId={(_r, i) => String(i)}
              selectable
              selectedIds={rowSel}
              onSelectionChange={(ids) => setRowSel(new Set(ids))}
              emptyText={emptyText}
              maximizable
              exportFilename={`formula-test-${kind}`}
              alwaysShowFooter
            />
          </div>
        </div>
      </div>

    </AppDialog>
  );
}
